import * as React from 'react';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';

import Maybe from 'graphql/tsutils/Maybe';
import { keyBy } from 'lodash-es';
import { useIntl } from 'react-intl';
import { Navigate, useLocation, useMatch, useNavigate } from 'react-router-dom';

import { FeatureEmptyState } from 'components/features/components/empty-state';
import useEffectOnceOnCondition from 'hooks/use-effect-once-on-condition';
import { useFeatureLoyaltyRewardListCategories } from 'hooks/use-feature-loyalty-reward-list-categories';
import { useFeatureLoyaltyUi } from 'hooks/use-feature-loyalty-ui';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import { RewardsListWidgetType } from 'pages/loyalty/loyalty-dashboard/types';
import { useAuthContext } from 'state/auth';
import { useRewardsEvaluation } from 'state/loyalty/hooks/use-loyalty-rewards-evaluation';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { EventName, emitEvent } from 'utils/event-hub';

import LoyaltyIncentiveNotAvailableModal from '../loyalty-incentives-components/loyalty-incentive-not-available-modal';
import { useFeaturesLoyaltyNoReward } from '../loyalty-no-reward-points/use-features-loyalty-no-reward';
import { LoyaltyNotEnrolled } from '../loyalty-not-enrolled';

import { EmptyStateContainer, StyledLoyaltyLoader } from './loyalty-rewards.styled';
import { LoyaltyRewardsView } from './loyalty-rewards.view';
import { NonNullableReward, NonNullableRewardCategory } from './types';

/**
 * Determines the category and reward that should be open by default based on the passed pre-selections
 * @param rewardCategories filtered categories
 * @param preselectedCategoryLabel pre-selected category label taken from the current route
 * @param preselectedRewardId pre-selected reward id taken from the current route
 */
export const getRewardCategoriesDefaultSelection = (
  rewardCategories: NonNullableRewardCategory[],
  preselectedCategoryLabel?: string | null,
  preselectedRewardId?: string
): [Maybe<NonNullableRewardCategory>, Maybe<NonNullableReward>] => {
  let selectedCategory;
  let selectedReward;
  let selectedRewardParentCategory;
  for (const category of rewardCategories) {
    if (preselectedCategoryLabel === category.label?.en) {
      selectedCategory = category;
    }
    for (const reward of category.rewards ?? []) {
      if (preselectedRewardId === reward?._id) {
        selectedReward = reward;
        selectedRewardParentCategory = category;
      }
    }
  }

  const defaultCategory = selectedReward
    ? selectedRewardParentCategory
    : selectedCategory || rewardCategories?.find(Boolean);
  const defaultReward = selectedReward || defaultCategory?.rewards?.find(Boolean);
  return [defaultCategory, defaultReward];
};

const useFilteredRewardCategories = () => {
  const { data: baseCategories } = useFeatureLoyaltyRewardListCategories();
  const { sanityRewardsMap, engineRewardsMap } = useLoyaltyRewardsList();

  const rewardCategories = useMemo(
    () =>
      baseCategories?.reduce((filteredCategories: NonNullableRewardCategory[], category) => {
        if (category?.rewards?.length && sanityRewardsMap) {
          const filteredRewards = category.rewards.filter(
            reward =>
              reward?._id &&
              sanityRewardsMap[reward._id] &&
              engineRewardsMap[reward?.loyaltyEngineId || '']
          );
          if (filteredRewards.length) {
            filteredCategories.push({ ...category, rewards: filteredRewards || [] });
          }
        }
        return filteredCategories;
      }, []),
    [baseCategories, engineRewardsMap, sanityRewardsMap]
  );

  return rewardCategories ?? [];
};

interface ILoyaltyRewardsProps {
  title?: string;
  isWidget?: boolean;
}

/**
 *
 * LoyaltyRewards displays a list of available rewards that can be redeemed at checkout
 *
 */

const LoyaltyRewards: FC<ILoyaltyRewardsProps> = ({ title, isWidget = false }) => {
  const { user } = useAuthContext();
  const [selectedRewardIdFromWidget, setSelectedRewardIdFromWidget] = useState('');
  const { data: noRewardsData, loading: noRewardsLoading } = useFeaturesLoyaltyNoReward();
  const { logRBIEvent } = useMParticleContext();

  const {
    rewards,
    engineRewardsMap,
    loading,
    sanityRewardsMap,
    refetchRewards,
  } = useLoyaltyRewardsList();

  useEffectOnceOnCondition(
    () => {
      emitEvent(EventName.SCREEN_RENDER, {
        screenId: 'loyaltyRewards',
      });
    },
    () => !selectedRewardId
  );

  const { rewardsEvaluationMap } = useRewardsEvaluation();
  const { featureLoyaltyUi, featureLoyaltyUiLoading } = useFeatureLoyaltyUi();

  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const isDesktop = useMediaQuery(MediaQuery.Desktop);

  let isCarousel = false;
  if (featureLoyaltyUi?.rewardsListWidgetType === RewardsListWidgetType.Carousel) {
    isWidget = true;
    isCarousel = true;
  }

  const { search } = useLocation();

  const { params } =
    useMatch({ path: `${formatMessage({ id: 'routes.loyaltyHome' })}/:tab/*` }) || {};

  // If using as widget, the reward ID is saved in state
  const selectedRewardId = isWidget ? selectedRewardIdFromWidget : params?.['*'];
  const rewardsTabPath = params?.tab!;
  const queryString = new URLSearchParams(search);
  const preselectedCategoryLabel = queryString.get('category');

  // Local state for opened accordions
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const rewardCategories = useFilteredRewardCategories();
  const rewardCategoriesMap = keyBy(rewardCategories, cat => cat?.label?.en);
  const [defaultCategory, defaultReward] = getRewardCategoriesDefaultSelection(
    rewardCategories,
    preselectedCategoryLabel,
    selectedRewardId
  );

  const handleRewardClick = useCallback(
    ({
      incentiveName,
      loyaltyEngineId,
      incentiveSanityId,
    }: {
      incentiveName: string;
      loyaltyEngineId: string;
      incentiveSanityId: string;
    }) => () => {
      if (selectedRewardId !== incentiveSanityId) {
        logRBIEvent({
          name: CustomEventNames.REWARD_SELECTED,
          type: EventTypes.Other,
          attributes: {
            engineId: loyaltyEngineId,
            sanityId: incentiveSanityId,
            name: incentiveName,
          },
        });
        if (isWidget) {
          setSelectedRewardIdFromWidget(incentiveSanityId);
          return;
        }
        navigate(incentiveSanityId);
      }
    },
    [isWidget, navigate]
  );

  // Set default opened accordion based on pre-selections
  useEffect(() => {
    const isSelectedCategoryAvailable = selectedCategories.some(cat => rewardCategoriesMap[cat]);
    if (!selectedCategories.length || !isSelectedCategoryAvailable) {
      const categoryLabel = defaultCategory?.label?.en;
      if (categoryLabel) {
        setSelectedCategories([categoryLabel]);
      }
    }
    // selectedCategories should not be in the deps, to prevent setting defaultCategory when all accordions are manually closed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCategory]);

  if (loading || noRewardsLoading || featureLoyaltyUiLoading) {
    return <StyledLoyaltyLoader />;
  }

  if (!user?.loyaltyId) {
    return <LoyaltyNotEnrolled />;
  }

  const selectedReward = sanityRewardsMap?.[selectedRewardId || ''];
  if (sanityRewardsMap && selectedRewardId && !selectedReward) {
    return <LoyaltyIncentiveNotAvailableModal />;
  }

  // Do not redirect if it's used as a widget
  // Redirect to first reward on current category if there isn't one pre-selected
  if (!isWidget && isDesktop && !selectedRewardId && rewards?.length && defaultReward) {
    return <Navigate to={defaultReward._id} replace />;
  }

  if (!rewardCategories?.length && noRewardsData && noRewardsData.noRewardsAvailable) {
    return (
      <EmptyStateContainer>
        <FeatureEmptyState content={noRewardsData.noRewardsAvailable} showCTA={false} />
      </EmptyStateContainer>
    );
  }

  return rewardCategories?.length && sanityRewardsMap ? (
    <LoyaltyRewardsView
      selectedReward={selectedReward}
      handleClick={handleRewardClick}
      sanityRewardsMap={sanityRewardsMap}
      rewardCategories={rewardCategories}
      engineRewardsMap={engineRewardsMap}
      formatMessage={formatMessage}
      rewardsTabPath={rewardsTabPath}
      selectedCategories={selectedCategories}
      setSelectedCategories={setSelectedCategories}
      refetchRewards={refetchRewards}
      rewardsEvaluationMap={rewardsEvaluationMap}
      isWidget={isWidget}
      isCarousel={isCarousel}
      title={title}
      setSelectedRewardIdFromWidget={setSelectedRewardIdFromWidget}
    />
  ) : null;
};

export default memo(LoyaltyRewards);
